import React, { useState } from "react";
import { useSelector } from "react-redux";
import ApiProxy from "../../../app/api/admin/proxy/ApiProxy";
import Modal from "../../components/modals/modal";
import Button from "../../components/button";
import InputTextarea from "../../components/inputs/textarea";
import InputCheckbox from "../../components/inputs/checkbox";
import Select from "../../components/inputs/select";
import ModalYesNo from "../../components/modals/modal-yes-no";

const LIST_PLACEHOLDER = 'socks5://login:password@1.2.3.4:1080\n5.6.7.8:3128:login:password';

// Ошибки строки {поле: [тексты]} → одна строка причин.
function formatLineErrors(errors) {
    return Object.values(errors ?? {}).flat().join('; ');
}

// Импорт списком (POST proxies/import): по прокси на строку, как их отдают продавцы. Импорт
// частичный — годные строки заводятся, остальные приходят с причиной, поэтому итог показываем
// в той же модалке, а не тостом. onDone — что-то завелось, список надо перечитать.
export default function ProxyImportModal({ onClose, onDone }) {
    const [_list, set_list] = useState('');
    const [_isActive, set_isActive] = useState(true);
    const [_proxyGroupId, set_proxyGroupId] = useState(''); // '' — без группы
    const [_mode, set_mode] = useState(''); // '' — режим каждого определит проверка
    const [_isBusy, set_isBusy] = useState(false);
    const [_errors, set_errors] = useState(null);
    const [_result, set_result] = useState(null);
    const proxyGroups = useSelector((state) => state.option.proxyGroups);
    const deviceProxyModes = useSelector((state) => state.option.deviceProxyModes);

    // Без группы прокси доступны устройствам ВСЕХ нод — это стоит подтвердить, а не получить
    // случайно, забыв выбрать группу.
    const [_isOpenModalNoGroup, set_isOpenModalNoGroup] = useState(false);

    const handleImportClick = () => {
        if (_isBusy || (_list.trim() === '')) return;
        if (_proxyGroupId === '') {
            set_isOpenModalNoGroup(true);
            return;
        }
        handleImport();
    };

    const handleImport = async () => {
        if (_isBusy || _list.trim() === '') return;
        set_isBusy(true);
        try {
            const api = ApiProxy.Import({ list: _list, is_active: _isActive, proxy_group_id: _proxyGroupId, mode: _mode });
            const data = await api.getResponse();
            // 422 на весь список (больше 500 строк и т.п.) — ничего не заведено, текст под полем
            set_errors(api.errors);
            if (data) {
                set_result(data);
                if (data.data.length > 0 && onDone) onDone();
            }
        } finally {
            set_isBusy(false);
        }
    };

    const handleReset = () => {
        set_list('');
        set_errors(null);
        set_result(null);
    };

    // Пока идёт импорт, модалку не закрываем: иначе ответ с причинами по строкам некуда показать,
    // а повторный импорт того же списка вернёт всё как «уже есть».
    return <Modal isOpenModal={true} closeModal={_isBusy ? () => {} : onClose} title="Импорт прокси"
        className="proxies__import">
        {_result === null ? <>
            <div className="proxies__hint">
                По прокси на строку: <code>протокол://логин:пароль@хост:порт</code> или
                <code>хост:порт:логин:пароль</code>, как отдают продавцы. Протоколы — socks5, socks5h,
                http; без протокола — socks5. Логин и пароль можно не указывать. Пустые строки и
                строки с <code>#</code> пропускаются. Не больше 500 строк. Уже заведённые
                пропускаются. Ни проверка, ни замер соединений сами не запускаются.
            </div>
            <InputTextarea className="input_wf proxies__import-list" value={_list} readOnly={_isBusy}
                placeholder={LIST_PLACEHOLDER} errors={_errors?.list} spellCheck={false}
                onChange={(e) => set_list(e.target.value)}>Список</InputTextarea>
            <Select className="input_wf" options={proxyGroups} value={_proxyGroupId} isNull={true} readOnly={_isBusy}
                errors={_errors?.proxy_group_id}
                onChange={(e) => set_proxyGroupId(e.target.value)}>Группа (одна на весь список)</Select>
            <Select className="input_wf" options={deviceProxyModes} value={_mode} isNull={true} readOnly={_isBusy}
                errors={_errors?.mode}
                onChange={(e) => set_mode(e.target.value)}>Режим (не выбран — определит проверка)</Select>
            <InputCheckbox value={_isActive} readOnly={_isBusy}
                onChange={(e) => set_isActive(e.target.checked)}>Завести активными</InputCheckbox>
            <div className="proxies__import-actions">
                <Button className="btn-success" disabled={_isBusy || _list.trim() === ''} onClick={handleImportClick}>
                    {_isBusy ? 'Импорт…' : 'Импортировать'}
                </Button>
            </div>
        </> : <ProxyImportResult result={_result} onRepeat={handleReset} onClose={onClose}/>}
        <ModalYesNo
            isOpenModal={_isOpenModalNoGroup}
            closeModal={() => set_isOpenModalNoGroup(false)}
            actionBtnYes={() => { set_isOpenModalNoGroup(false); handleImport(); }}
            title="Завести прокси без группы? Без группы они доступны устройствам всех нод."
        />
    </Modal>;
}

// Итог: «заведено N, пропущено M, с ошибками K», под ним — строки с причинами.
function ProxyImportResult({ result, onRepeat, onClose }) {
    return <>
        <div className="proxies__import-summary">
            <span className="badge badge_online">заведено {result.data.length}</span>
            <span className="badge">пропущено {result.skipped.length}</span>
            <span className={`badge ${result.errors.length > 0 ? 'badge_offline' : ''}`}>с ошибками {result.errors.length}</span>
        </div>

        {result.errors.length > 0 ? <>
            <div className="proxies__check-title">С ошибками</div>
            <ul className="proxies__import-lines">
                {result.errors.map((item) => <li key={item.line}>
                    <span className="proxies__import-line">строка {item.line}</span> {item.proxy} —{' '}
                    <span className="proxies__check-error">{formatLineErrors(item.errors)}</span>
                </li>)}
            </ul>
        </> : null}

        {result.skipped.length > 0 ? <>
            <div className="proxies__check-title">Пропущено</div>
            <ul className="proxies__import-lines">
                {result.skipped.map((item) => <li key={item.line}>
                    <span className="proxies__import-line">строка {item.line}</span> {item.proxy} — {item.reason}
                </li>)}
            </ul>
        </> : null}

        {result.data.length > 0
            ? <div className="proxies__hint">Заведённые ещё не проверены — выберите их в списке и «Проверить».</div>
            : null}

        <div className="proxies__import-actions">
            <Button onClick={onRepeat}>Импортировать ещё</Button>
            <Button className="btn-success" onClick={onClose}>Готово</Button>
        </div>
    </>;
}
